import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from "@ellucian/react-design-system/core";
import { appConfig } from "../../utils/config";
import { handleLogout } from "../../pages/RouterPage";
import "./styles/RequestInfoDialog.css";
const unifiedFontStack =
  '"Proxima Nova", Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif';
const unifiedFontStyles = {
  fontFamily: unifiedFontStack,
  fontSize: 16,
  fontWeight: 400,
  lineHeight: "125%",
  letterSpacing: "0.08px"
};
interface RequestInstallDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  onError?: (message: string) => void;
  solution: any;
  /** ERP type for the account — passed down from SolutionHeader which already owns the status */
  erpType: string;
}
const RequestInstallDialog: React.FC<RequestInstallDialogProps> = ({
  open,
  onClose,
  onConfirm,
  onError,
  solution,
  erpType,
}) => {
  const [tenants, setTenants] = useState<any[]>([]);
  const [selectedTenant, setSelectedTenant] = useState("");
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [step, setStep] = useState(1); // 1 = select tenant, 2 = review selection
  const [apiError, setApiError] = useState<string | null>(null);
  useEffect(() => {
    const tenantsData = localStorage.getItem("tenantsData");
    if (tenantsData) {
      const parsedTenants = JSON.parse(tenantsData);
      setTenants(parsedTenants);
      if (parsedTenants.length > 0) {
        setSelectedTenant(parsedTenants[0].id); // Always select the first tenant by default
      }
    }
  }, [open]); // Trigger this effect whenever the dialog opens
  useEffect(() => {
    if (!open) {
      setSelectedTenant("");
      setTouched(false);
      setSubmitAttempted(false);
      setStep(1);
      setApiError(null);
    }
  }, [open]);

  const validationError =
    !selectedTenant || selectedTenant.trim() === ""
      ? "Please select a tenant to continue."
      : "";
  const showError =
    (submitAttempted || touched) && validationError && step === 1;
  const handleConfirm = async () => {
    setSubmitAttempted(true);
    if (!selectedTenant || selectedTenant.trim() === "") {
      return;
    }
    setLoading(true);
    setApiError(null);
    const tenant = tenants.find((t) => t.id === selectedTenant);
    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
    // erpType: prefer prop (from useSelfEntitlement hook), fall back to localStorage cache (survives refresh)
    const resolvedErpType =
      erpType ||
      (() => {
        try {
          const cached = localStorage.getItem("marketplace_self_entitlement_status");
          return cached ? JSON.parse(cached)?.erpType || "" : "";
        } catch {
          return "";
        }
      })();
    // artifactEnvironment: Zapp only has two env_id values — "prod" (Production) and "dev" (everything else)
    const normalizeEnv = (label: string) => {
      const lower = label.toLowerCase();
      return lower === "production" || lower === "prod" ? "prod" : "dev";
    };
    const artifactEnvironment = normalizeEnv(tenant.label || "");
    const payload = {
      sku: solution.storefrontIntegrationId,
      productName: solution.name,
      tenantId: tenant.id,
      tenantLabel: tenant.label,
      tenantAlias: tenant.alias,
      institutionName: tenant.name,
      userName: localStorage.getItem("userName") || "",
      userEmail: userInfo.email || "",
      erpType: resolvedErpType,
      artifactEnvironment,
    };
    try {
      const response = await fetch(`${appConfig.api.baseUrl}/api/jobs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "cookie-based-auth", // Required for API Gateway to invoke authorizer with cookie-only auth
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        onConfirm && onConfirm();
        onClose();
      } else if (response.status === 401) {
        handleLogout();
        return;
      } else {
        onClose();
        onError?.('Installation failed. Please contact support if this persists.');
      }
    } catch (err) {
      onClose();
      onError?.('Installation failed. Please contact support if this persists.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Dialog
        open={open}
        onClose={(
          e: React.SyntheticEvent,
          reason?: "backdropClick" | "escapeKeyDown"
        ) => reason !== "backdropClick" && onClose()}
        showTitleBarClose={true}
        maxWidth={false}
        PaperProps={{
          sx: step === 2
            ? {
                display: 'flex',
                width: { xs: 'calc(100% - 32px)', sm: '700px' },
                maxWidth: { xs: 'calc(100% - 32px)', sm: '700px' },
                borderRadius: '4px',
                boxShadow: '0px 8px 32px 0px rgba(0,0,0,0.16)',
                flexDirection: 'column',
                background: '#fff',
                p: { xs: '16px', sm: '24px' },
                margin: { xs: '16px', sm: 'auto' },
                position: 'relative',
              }
            : {
                maxWidth: 840,
                minHeight: 520,
                borderRadius: "10px"
              }
        }}
        BackdropProps={{
          sx: {
            background: 'rgba(0, 0, 0, 0.30) !important',
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 0,
            pt: '24px',
            px: '24px',
            textAlign: 'left',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif',
              fontSize: '24px',
              fontWeight: 600,
              fontStyle: 'normal',
              lineHeight: '1.2',
              color: 'var(--Neutral-neutral-600, #151618)',
              wordBreak: 'break-word',  
              display: 'block',
              maxWidth: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 56px)' },
            }}
          >
            {`Install ${solution.name}?`}
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            px: '32px',
            pt: 0,
            pb: '24px',
            overflow: "visible"
          }}
        >
          {step === 1 && (
            <>
              {tenants.length === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    mb: 3
                  }}
                >
                  <Typography
                    sx={{
                      ...unifiedFontStyles,
                      fontSize: 16,
                      color: "#6B7280",
                      mb: 2
                    }}
                  >
                    No tenant environments are currently attached to your user
                    account for this solution. Please contact your administrator
                    or request more information to explore availability options.
                  </Typography>
                </Box>
              )}
              {tenants.length > 0 && (
                <Box>
                  <Typography
                    sx={{
                      ...unifiedFontStyles,
                      mb: 3,
                      color: "#151618"
                    }}
                  >
                    Choose the environment to install into
                  </Typography>
                  <Typography
                    sx={{
                      ...unifiedFontStyles,
                      fontSize: 14,
                      color: "#151618",
                      borderRadius: "4px",
                      display: "block",
                      py: 1.5,
                      mb: 2
                    }}
                  >
                    <strong>Note: </strong>
                    Marketplace installations are currently supported only for eligible SaaS environments.{' '}
                    Installation is not yet supported for Banner On-Prem, Banner Managed Cloud, Colleague
                    On-Prem, or Colleague Managed Cloud environments. We are actively working to add support
                    for these environments in a future release.
                  </Typography>
                  <Table sx={{ background: "#FFFFFF" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            width: 72,
                            height: 48,
                            textAlign: "center",
                            background: "#FFFFFF"
                          }}
                        ></TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            height: 44,
                            paddingTop: "13px",
                            paddingBottom: "13px",
                            paddingLeft: "16px",
                            paddingRight: "24px",
                            gap: "10px"
                          }}
                        >
                          Tenant Name
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            height: 44,
                            paddingTop: "13px",
                            paddingBottom: "13px",
                            paddingLeft: "16px",
                            paddingRight: "24px",
                            gap: "10px"
                          }}
                        >
                          Tenant ID
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tenants.map((tenant) => (
                        <TableRow
                          key={tenant.id}
                          sx={{
                            background: "#FFFFFF",
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                              borderBottom: "1px solid #F3F4F6"
                            }
                          }}
                        >
                          <TableCell
                            sx={{
                              width: 72,
                              height: 48,
                              textAlign: "center",
                              padding: 0,
                              background: "#FFFFFF"
                            }}
                          >
                            <input
                              type="radio"
                              name="tenant-selection"
                              checked={selectedTenant === tenant.id}
                              onChange={() => setSelectedTenant(tenant.id)}
                              style={{
                                margin: 0,
                                cursor: "pointer",
                                width: "16px",
                                height: "16px",
                                accentColor: "#026BC8"
                              }}
                              aria-label={`Select ${tenant.label}`}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              height: 44,
                              paddingTop: "13px",
                              paddingBottom: "13px",
                              paddingLeft: "16px",
                              paddingRight: "24px",
                              gap: "10px"
                            }}
                          >
                            {tenant.label} ({tenant.alias})
                          </TableCell>
                          <TableCell
                            sx={{
                              height: 44,
                              paddingTop: "13px",
                              paddingBottom: "13px",
                              paddingLeft: "16px",
                              paddingRight: "24px",
                              gap: "10px"
                            }}
                          >
                            {tenant.id}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {showError && (
                    <Typography
                      sx={{
                        mt: 2,
                        fontFamily: unifiedFontStack,
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#d32f2f"
                      }}
                    >
                      {validationError}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
          {step === 2 && (
            <>
              <Typography
                sx={{
                  color: 'var(--Neutral-neutral-600, #151618)',
                  fontFamily: unifiedFontStack,
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '125%',
                  letterSpacing: '0.08px',
                  background: '#fff',
                  py: '4px',
                  m: 0,
                  textAlign: 'left'
                }}
              >
                You are about to install <strong>{solution.name}</strong> to the <strong>{(() => { const t = tenants.find((t) => t.id === selectedTenant); return t ? `${t.label} (${t.alias})` : 'selected'; })()}</strong> environment.
                <br />
                <br />
                This action will begin the automated deployment process for this integration. Some manual configuration steps may still be required after installation.
                <br />
                <br />
                Do you want to continue?
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2
          }}
        >
          {step === 1 && (
            <Box sx={{ display: "flex", gap: 2, marginLeft: "auto" }}>
              <Button onClick={onClose} color="secondary" disabled={loading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (!selectedTenant || selectedTenant.trim() === "") {
                    setSubmitAttempted(true);
                    setTouched(true);
                    return;
                  }
                  setStep(2);
                }}
                disabled={tenants.length === 0}
              >
                Continue
              </Button>
            </Box>
          )}
          {step === 2 && (
            <>
              {/* Left-aligned Back button */}
              <Box sx={{ flex: 1 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setStep(1)}
                  sx={{
                    height: '40px',
                    fontWeight: 600,
                    fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    lineHeight: '110%',
                    // textTransform: 'capitalize',
                    boxShadow: 'none',
                    boxSizing: 'border-box',
                    border: '1px solid var(--Interactive-Blue-cta-blue-600, #026BC8) !important',
                    color: 'var(--Interactive-Blue-cta-blue-600, #026BC8) !important',
                    background: 'transparent',
                    padding: '0 16px',
                    '&:hover': {
                      background: '#F5F8FA',
                      border: '1px solid var(--Interactive-Blue-cta-blue-600, #026BC8) !important',
                      color: 'var(--Interactive-Blue-cta-blue-600, #026BC8) !important',
                      boxShadow: 'none',
                    },
                    '&:focus': {
                      outline: 'none',
                      boxShadow: 'none',
                      background: 'transparent',
                      color: 'var(--Interactive-Blue-cta-blue-600, #026BC8) !important',
                    },
                    '&:active': {
                      outline: 'none',
                      boxShadow: 'none',
                      background: 'transparent',
                      color: 'var(--Interactive-Blue-cta-blue-600, #026BC8) !important',
                    },
                  }}
                >
                  Back
                </Button>
              </Box>
              {/* Right-aligned Cancel and Confirm Request */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  onClick={onClose}
                  color="secondary"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfirm}
                  disabled={loading}
                  sx={{
                    height: '40px',
                    fontWeight: 600,
                    fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    lineHeight: '110%',
                    // textTransform: 'capitalize',
                    color: 'var(--Neutral-neutral-100, #FFF) !important',
                    boxShadow: 'none',
                    boxSizing: 'border-box',
                    padding: '0 16px',
                  }}
                >
                  {loading ? 'Confirming...' : 'Confirm Request'}
                </Button>
              </Box>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
export default RequestInstallDialog;
